/** Se o navegador perdeu a rede (SPEC-FE-001 § 6).
 *
 * O board é um read model que relê sozinho a cada ciclo. Sem rede, a releitura
 * falha em silêncio e a tela continua mostrando o funil de minutos atrás — com
 * a mesma cara de sempre. A pessoa move um card, o `PATCH` falha, e o erro que
 * ela lê é "não foi possível mover", que sugere problema no servidor ou na
 * permissão. O aviso troca isso por um diagnóstico que ela pode resolver.
 *
 * **`navigator.onLine` diz pouco, e isso está certo.** Ele responde `false`
 * apenas quando o sistema afirma não haver interface de rede — Wi-Fi caído,
 * modo avião. Um cabo ligado num roteador sem internet continua `true`. Serve
 * para o caso que ele reconhece, e o erro da requisição continua cobrindo o
 * resto; um aviso que tentasse adivinhar conectividade real ficaria pedindo
 * `ping` a alguém, e um falso "você está sem internet" é pior que nenhum.
 */

import { useEffect, useState } from "react";

export function useOffline(): boolean {
  // Inicializa lendo o estado atual, e não `false`: quem abre a aba já sem
  // rede precisa ver o aviso na primeira renderização, não no próximo evento
  // — que pode nunca chegar.
  const [offline, setOffline] = useState(() => !navegadorOnline());

  useEffect(() => {
    function atualizar() {
      setOffline(!navegadorOnline());
    }
    window.addEventListener("online", atualizar);
    window.addEventListener("offline", atualizar);
    // Entre a primeira renderização e este efeito a rede pode ter mudado.
    atualizar();
    return () => {
      window.removeEventListener("online", atualizar);
      window.removeEventListener("offline", atualizar);
    };
  }, []);

  return offline;
}

/** `onLine` ausente conta como online.
 *
 * Ambiente sem a propriedade (jsdom antigo, runtime não-navegador) não deve
 * abrir um aviso de "sem internet" que ninguém pediu.
 */
function navegadorOnline(): boolean {
  return typeof navigator === "undefined" || navigator.onLine;
}
